/**
 * Client half of the universal attachment-content resolver (2026-08-02).
 *
 * Signed GCS attachment URLs work for navigation (open in tab / download) but not
 * for browser fetch() — the bucket serves no CORS headers — so the previewer's
 * byte-needing renderers (PDF, DOCX, sheets, text, HTML) degraded in production.
 * When direct fetch fails, PreviewContent falls back here: an authenticated POST
 * to prest0nResolveAttachmentContent, which reads the object server-side and
 * returns it inline. Mirrors resolve3d.ts (same endpoint family, same auth).
 */
import { getAuth } from 'firebase/auth';
import { CF_BASE_URL } from './resolve3d';
import type { SerializedAttachment } from '../GlobalChatSystem/types';

export interface ResolvedAttachmentContent {
  bytes: ArrayBuffer;
  contentType: string;
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out.buffer;
}

/**
 * Message context forwarded to the resolver so a record with NO usable
 * locator (the race shapes: optimistic bubble before upload upgrade, doc
 * echoes, mirrors that dropped fields) can still be found server-side by
 * reconstructing the storage paths the platform writes (2026-08-09).
 */
export interface AttachmentResolveContext {
  messageId?: string;
  /**
   * Shared thread the message lives in. Lets a PARTICIPANT read a teammate's
   * uploaded photo server-side (owner report 2026-09-07) — the server checks
   * live participation and that the object was posted in this very message,
   * so a signed URL expiring after 7 days no longer loses the picture.
   */
  threadId?: string;
  sessionId?: string;
  turnId?: string;
  /**
   * The turn that carried the file (2026-10-07). An upload starts when the
   * file is picked, under a folder nothing in the message names; the turn's
   * own record names it, so the server finds the object through the turn for
   * the person who sent it. A bare task id, or the message's shared id
   * (`prest0n_task_<id>__user`).
   */
  taskId?: string;
  /**
   * A held card's file (2026-10-10, "the files should be previewed within the
   * card"): the card's workspace, its hold and the file's key. The server opens
   * it for the card's asker, or for someone in the card's room who may see the
   * account it acts through (functions heldFileRead.ts) — never by a path.
   */
  held?: HeldFileRef;
}

/** One file a held card names, as its room record and its own copy carry it. */
export interface HeldFileRef {
  accountId: string;
  actionId: string;
  fileKey: string;
}

const isHeldFileRef = (v: unknown): v is HeldFileRef => {
  const r = v as Partial<HeldFileRef> | null | undefined;
  return !!r && typeof r.accountId === 'string' && !!r.accountId
    && typeof r.actionId === 'string' && !!r.actionId
    && typeof r.fileKey === 'string' && !!r.fileKey;
};

export { attachmentResolveContextOf, taskIdOfMessage } from './attachmentTurn';

/** True when the attachment carries ANY source the server resolver can use —
 *  a direct locator, the cloud-ingest gcsPath, or reconstructable context. */
export function canResolveServerSide(
  serialized?: SerializedAttachment | null,
  context?: AttachmentResolveContext | null,
): boolean {
  if (!serialized) return false;
  if (serialized.storagePath || serialized.storageUrl || serialized.gcsPath) return true;
  if (isHeldFileRef(context?.held)) return true;
  return Boolean(
    serialized.name && context && (context.messageId || (context.sessionId && context.turnId) || context.taskId),
  );
}

/** The request body both modes send — one shape, so the two lanes can never
 *  disagree about which object they are asking about. */
function resolverRequestBody(
  serialized: SerializedAttachment,
  context: AttachmentResolveContext | null | undefined,
  mode: 'bytes' | 'url',
  disposition?: 'attachment',
): string {
  return JSON.stringify({
    mode,
    // Omitted for display, so the picture lane's body is byte-identical.
    ...(disposition ? { disposition } : {}),
    storagePath: serialized.storagePath || undefined,
    gcsPath: serialized.gcsPath || undefined,
    storageUrl: serialized.storagePath ? undefined : serialized.storageUrl || undefined,
    name: serialized.name || undefined,
    ...(context && (context.messageId || (context.sessionId && context.turnId) || context.taskId || isHeldFileRef(context.held))
      ? {
        context: {
          messageId: context.messageId || undefined,
          threadId: context.threadId || undefined,
          sessionId: context.sessionId || undefined,
          turnId: context.turnId || undefined,
          taskId: context.taskId || undefined,
          name: serialized.name || undefined,
          // A held card's file is named by its card and key; only those three ids travel.
          ...(isHeldFileRef(context.held)
            ? { held: { accountId: context.held.accountId, actionId: context.held.actionId, fileKey: context.held.fileKey } }
            : {}),
        },
      }
      : {}),
  });
}

async function postToResolver(
  serialized: SerializedAttachment,
  context: AttachmentResolveContext | null | undefined,
  mode: 'bytes' | 'url',
  disposition?: 'attachment',
): Promise<Response> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('auth-required');
  const token = await user.getIdToken();
  return fetch(`${CF_BASE_URL}/prest0nResolveAttachmentContent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: resolverRequestBody(serialized, context, mode, disposition),
  });
}

export async function resolveAttachmentContent(
  serialized: SerializedAttachment,
  context?: AttachmentResolveContext | null,
): Promise<ResolvedAttachmentContent> {
  const resp = await postToResolver(serialized, context, 'bytes');
  if (!resp.ok) throw new Error(resp.status === 404 ? 'not-found' : `resolver-${resp.status}`);
  const data = (await resp.json()) as { base64?: string; contentType?: string };
  if (!data || typeof data.base64 !== 'string') throw new Error('resolver-malformed');
  return {
    bytes: base64ToArrayBuffer(data.base64),
    contentType: typeof data.contentType === 'string' ? data.contentType : '',
  };
}

/**
 * A DISPLAYABLE URL for the same authorized object (2026-09-12, part 5).
 *
 * The byte lane above answers a POST with the whole file base64-encoded, so
 * nothing about it is cacheable — every recipient pays a Cloud Function
 * invocation and a main-thread `atob` for every view of every image. This
 * asks the same endpoint, with the same authorization, for a short-lived
 * signed read URL instead; the browser then loads the object from GCS through
 * its ordinary image pipeline (HTTP cache, CDN, off-thread decode).
 *
 * `<img src>` is a plain image load rather than a CORS fetch, so the missing
 * bucket CORS headers that made the byte lane necessary do not apply here —
 * which is exactly why this is for DISPLAY and the previewer keeps the bytes.
 *
 * Null, never a throw, whenever the URL shape is not available: an older
 * deployment that answers with base64, a mint that failed server-side, or a
 * malformed body. The caller climbs to the byte rung, so the picture still
 * arrives — one round trip later, as it always did.
 */
/**
 * A signed URL that SAVES rather than renders — the only route by which an
 * attachment over the resolver's byte cap can reach a person at all.
 *
 * Measured 2026-09-23 on a 998.6 MB PDF in a shared thread: the download ladder
 * has no rung for a cloud-ingest record (gcsPath, no storageUrl/storagePath),
 * so it fell to the BYTE resolver, which refuses anything over
 * ATTACHMENT_PREVIEW_MAX_BYTES (20 MiB) with a 413 — surfaced to the person as
 * "the file may have been deleted", of a file that was sitting in the bucket.
 *
 * URL mode has no such cap (its own comment says why: the cap exists because
 * base64 rides a JSON body, and a URL has no body), and the request body has
 * always carried `gcsPath`. So nothing new is authorized here and no bytes ride
 * the Function — the one new thing is asking for the disposition that makes a
 * browser save a renderable type instead of navigating to it (R17).
 *
 * Null, never a throw, when the shape is unavailable — an older deployment
 * answers without `url` and the caller climbs to its next rung.
 */
export async function resolveAttachmentDownloadUrl(
  serialized: SerializedAttachment,
  context?: AttachmentResolveContext | null,
): Promise<string | null> {
  const resp = await postToResolver(serialized, context, 'url', 'attachment');
  if (!resp.ok) throw new Error(resp.status === 404 ? 'not-found' : `resolver-${resp.status}`);
  const data = (await resp.json()) as { mode?: string; url?: string };
  if (!data || data.mode !== 'url' || typeof data.url !== 'string' || !data.url) return null;
  return data.url;
}

export async function resolveAttachmentDisplayUrl(
  serialized: SerializedAttachment,
  context?: AttachmentResolveContext | null,
): Promise<string | null> {
  const resp = await postToResolver(serialized, context, 'url');
  if (!resp.ok) throw new Error(resp.status === 404 ? 'not-found' : `resolver-${resp.status}`);
  const data = (await resp.json()) as { mode?: string; url?: string };
  if (!data || data.mode !== 'url' || typeof data.url !== 'string' || !data.url) return null;
  return data.url;
}

/** Where a stored object lives, as a file record carries it. */
export interface AttachmentLocator {
  storagePath?: string;
  gcsPath?: string;
}

const LOCATOR_PREFIX_RE = /^(users|turns|ingest)\/[^/]+\/./;

/**
 * Where the person's OWN file is, for a record that lost its location
 * (2026-10-07). The same url-mode request the picture lane makes; the server
 * names the object's path only for the caller's own file. Null when it named
 * none (an older deployment, someone else's file, a failed mint).
 */
export async function locateAttachment(
  serialized: SerializedAttachment,
  context?: AttachmentResolveContext | null,
): Promise<AttachmentLocator | null> {
  const resp = await postToResolver(serialized, context, 'url');
  if (!resp.ok) throw new Error(resp.status === 404 ? 'not-found' : `resolver-${resp.status}`);
  const data = (await resp.json()) as { storagePath?: unknown; gcsPath?: unknown };
  const pick = (v: unknown): string | undefined =>
    typeof v === 'string' && LOCATOR_PREFIX_RE.test(v) && !v.includes('..') ? v : undefined;
  const storagePath = pick(data?.storagePath);
  const gcsPath = pick(data?.gcsPath);
  if (!storagePath && !gcsPath) return null;
  return { ...(storagePath ? { storagePath } : {}), ...(gcsPath ? { gcsPath } : {}) };
}
